import React, { useState } from 'react';
import { BadgeCheck, ChevronLeft, ChevronRight, Heart, MapPin, Scale, Star, MessageCircle, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency, cn } from '../../lib/utils';
import { DEFAULT_DEALER_AVATAR, resolveGem, gemAt } from '../../constants/sampleImages';

export const ListingCard = ({ listing, variant = 'grid'  }) => {
  const isSold = listing.status === 'SOLD';
  const images = (listing.images?.length ? listing.images : [null]).map((img, i) => resolveGem(img, i));
  const [activeIndex, setActiveIndex] = useState(0);
  const hasMultipleImages = images.length > 1;

  const goToImage = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(index);
  };

  return (
    <div className={cn(
      "bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden group hover:shadow-xl hover:border-slate-300 transition-all duration-500",
      variant === 'feed' ? "mb-6" : ""
    )}>
      {/* Enhanced Vendor Header */}
      <div className="p-4 flex items-center justify-between bg-slate-50/50">
        <Link to={`/vendors/${listing.vendorId}`} className="flex items-center gap-2 md:gap-3 group/vendor min-w-0">
          <div className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm shrink-0 transition-transform group-hover/vendor:scale-105">
             <img src={DEFAULT_DEALER_AVATAR} className="w-full h-full object-cover" alt="Dealer" />
          </div>
          <div className="flex flex-col min-w-0 leading-tight">
            <span className="text-[8px] md:text-[10px] font-black text-primary uppercase tracking-widest mb-0.5">Verified Dealer</span>
            <span className="text-xs md:text-sm font-bold text-slate-900 group-hover/vendor:text-primary transition-colors truncate">Ceylon Gemstone Hub</span>
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={8} className="text-slate-400 md:hidden" />
              <MapPin size={10} className="text-slate-400 hidden md:block" />
              <span className="text-[8px] md:text-[10px] font-medium text-slate-500">Beruwala, SL</span>
            </div>
          </div>
        </Link>
      </div>

      <div className="relative aspect-square overflow-hidden bg-slate-100">
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {images.map((src, i) => (
            <Link
              key={i}
              to={`/listings/${listing.id}`}
              className="block min-w-full h-full shrink-0"
            >
              <img
                src={src}
                alt={`${listing.title || listing.name} — image ${i + 1}`}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = gemAt(i);
                }}
                className={cn(
                  "w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000",
                  isSold && "grayscale-[0.5] opacity-80"
                )}
              />
            </Link>
          ))}
        </div>

        {hasMultipleImages && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={(e) => goToImage(e, (activeIndex - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 backdrop-blur-md text-slate-700 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={(e) => goToImage(e, (activeIndex + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 backdrop-blur-md text-slate-700 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`View image ${i + 1}`}
                  onClick={(e) => goToImage(e, i)}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === activeIndex ? "w-4 bg-white shadow-sm" : "w-1.5 bg-white/60 hover:bg-white/80"
                  )}
                />
              ))}
            </div>
          </>
        )}

        {/* Status Overlays */}
        <div className="absolute inset-0 pointer-events-none">
          {isSold && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[2px]">
              <div className="rotate-[-12deg] px-8 py-2 border-4 border-white text-white font-black text-3xl uppercase tracking-[0.2em] shadow-2xl">
                Sold
              </div>
            </div>
          )}
        </div>

        <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
          {listing.featured && !isSold && (
            <span className="bg-amber-400 text-white text-[9px] font-black px-3 py-1 rounded-full uppercase shadow-lg tracking-widest">Premium</span>
          )}
          {(listing.certification !== 'None' || (listing).certificate?.verified) && (
            <span className="bg-primary text-white text-[9px] font-black px-3 py-1 rounded-full uppercase shadow-lg flex items-center gap-1.5 tracking-widest">
              <BadgeCheck size={10} /> Certified
            </span>
          )}
        </div>

        {!isSold && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className="absolute bottom-4 right-4 z-10 p-3 bg-white/90 backdrop-blur-md rounded-2xl text-slate-500 hover:text-red-500 transition-all shadow-xl active:scale-90 group/save"
          >
            <Heart size={20} className="group-active/save:fill-red-500 transition-colors" />
          </button>
        )}
      </div>

      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0 flex-1 pr-2">
             <Link to={`/listings/${listing.id}`}>
               <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors text-base md:text-lg leading-tight truncate">
                 {listing.title || `${listing.variety} ${listing.type}`}
               </h3>
             </Link>
             <div className="flex items-center gap-2 md:gap-3 mt-1.5 flex-wrap">
               <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-[8px] md:text-[10px] font-bold text-slate-500">
                 <Scale size={10} className="md:w-3 md:h-3" />
                 <span>{listing.caratWeight || listing.weight} ct</span>
               </div>
               <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-[8px] md:text-[10px] font-bold text-slate-500">
                 <MapPin size={10} className="md:w-3 md:h-3" />
                 <span>{listing.origin}</span>
               </div>
             </div>
          </div>
          <div className="text-right shrink-0">
            {!isSold ? (
              <>
                <p className="text-[8px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Asking Price</p>
                <p className="text-base md:text-xl font-black text-accent tracking-tighter">{formatCurrency(listing.price, listing.currency)}</p>
              </>
            ) : (
              <span className="text-[8px] md:text-[10px] font-black text-primary bg-primary/5 px-2 py-1 rounded-lg border border-primary/10 uppercase tracking-tighter">Sold</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
           <div className="flex items-center gap-1.5">
              <div className="flex text-amber-400">
                <Star size={12} fill="currentColor" />
                <Star size={12} fill="currentColor" />
                <Star size={12} fill="currentColor" />
                <Star size={12} fill="currentColor" />
                <Star size={12} fill="currentColor" />
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">4.9 (24)</span>
           </div>
           <div className="flex items-center gap-3">
              <button className="text-slate-400 hover:text-primary transition-colors">
                <MessageCircle size={18} />
              </button>
              <button className="text-slate-400 hover:text-primary transition-colors">
                <Share2 size={18} />
              </button>
           </div>
        </div>
      </div>
    </div>
  );
};

